import { computeVariantUrl } from 'yoda-site-components/lib/helpers/Utils/VariantUrlHelper';

export const buildProductGroupSchema = (
    productDetails,
    lotPriceInventory,
    baseUrl,
    valuation,
    priceData = []
) => {
    const { id, name, brand, category, dimensions = [], lots = [] } = productDetails;
    const meta = productDetails.meta || {};

    const colorDimension = dimensions.find((dim) => dim.id === 'COLOR');
    const sizeDimension = dimensions.find(
        (dim) => dim.id === 'SIZE' || dim.id === 'WAIST' || dim.id === 'CHEST'
    );

    if (!colorDimension && !sizeDimension) return null;

    // Create a clean schema object with only the properties we want
    const schema = {
        '@context': 'https://schema.org',
        '@type': 'ProductGroup',
        '@id': `${baseUrl}#productgroup`,
        url: baseUrl || '',
        name,
        description: meta.description || '',
        productGroupID: id,
        brand: { '@type': 'Organization', name: brand?.name || '' },
        category: category?.name || '',
        variesBy: [],
        additionalProperty: {
            '@type': 'PropertyValue',
            name: 'Shipping information',
            value: 'https://www.jcpenney.com/m/customer-service/shipping-information',
        },
        hasMerchantReturnPolicy: {
            '@type': 'MerchantReturnPolicy',
            url: 'https://www.jcpenney.com/m/customer-service/return-policy',
        },
    };

    if (colorDimension) schema.variesBy.push('color');
    if (sizeDimension) schema.variesBy.push('size');

    if (valuation?.rating && valuation?.reviews?.count) {
        schema.aggregateRating = {
            '@type': 'AggregateRating',
            ratingValue: parseFloat(valuation.rating),
            reviewCount: parseInt(valuation.reviews.count, 10),
        };
    }

    const variants = [];

    lots.forEach((lot) => {
        lot.items?.forEach((item) => {
            const { id: sku, optionValues = [] } = item;
            let color = null;
            let size = null;
            let variantImage = null;

            optionValues.forEach((option) => {
                if (colorDimension?.options.find((opt) => opt.id === option.id)) {
                    color = option.value;
                    variantImage = option.image?.url || option.productImage?.url;
                }
                if (sizeDimension?.options.find((opt) => opt.id === option.id)) {
                    size = option.value;
                }
            });

            if (!color && !size) return;

            const variantParts = [name];
            if (color) variantParts.push(color);
            if (size) variantParts.push(size);

            const variant = {
                '@type': 'Product',
                name: variantParts.join(' - '),
                sku,
                url: computeVariantUrl(baseUrl, color, size) || baseUrl,
            };

            if (color) variant.color = color;
            if (size) variant.size = size;
            if (variantImage) variant.image = [variantImage];

            const variantPrice = priceData.find((price) => price.sku === sku);
            // Always add offers, use variant price or fallback
            variant.offers = {
                '@type': 'Offer',
                price: variantPrice?.min || '0.00',
                priceCurrency: 'USD',
                availability: variantPrice
                    ? 'https://schema.org/InStock'
                    : 'https://schema.org/OutOfStock',
                itemCondition: 'https://schema.org/NewCondition',
                seller: { '@type': 'Organization', name: 'JCPenney' },
            };

            variants.push(variant);
        });
    });

    if (variants.length > 0) schema.hasVariant = variants;

    /* istanbul ignore next */
    // Only aggregateRating should be present for reviews
    if (schema.review || schema.reviews) {
        delete schema.review;
        delete schema.reviews;
    }

    /* istanbul ignore next */
    // Remove any properties that might contain individual review data
    Object.keys(schema).forEach((key) => {
        if (key !== 'aggregateRating' && schema[key] && typeof schema[key] === 'object') {
            if (
                schema[key].dateCreated ||
                schema[key].datePublished ||
                schema[key].reviewBody ||
                schema[key].headline
            ) {
                delete schema[key];
            }
        }
    });

    return schema;
};
